import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useStyles } from 'app/components/Sidebar/RightSidebar/RightSidebar.styles';
import { SidebarItem } from 'app/components/Sidebar/SidebarItem';
import { SidebarMenuLink } from 'app/components/Sidebar/SidebarMenuLink';
import { useRightSideMenu } from 'app/hooks/right-side-menu';
import { UserEntity } from 'app/interfaces/entity';
import { NavigationMenuItem } from 'app/interfaces/sidebar';
import { sessionSelector } from 'app/store/session/selectors';
import LogoutPopup from 'container/components/LogoutPopup';
import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ActionGuard } from 'shared/components/ActionGuard';
import { UserAvatar } from 'shared/components/UserAvatar';
import { GuestSidebarHeader } from '../GuestSidebarHeader';

export const RightSidebar: FC = () => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const user = useSelector(sessionSelector.activeUser);
  const isAuthorized = useSelector(sessionSelector.isAuthorized);

  const onOpenHandler = useCallback(
    (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget),
    [setAnchorEl]
  );
  const onCloseHandler = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const {
    myProfileMenu,
    authorizedActionsMenu,
    nonAuthorizedActionsMenu,
    linksMenu,
    isLogoutOpen,
    onLogoutHandler,
  } = useRightSideMenu();

  const onClickHandler = useCallback(
    (menuItem: NavigationMenuItem) => {
      onCloseHandler();
      menuItem?.onClick();
    },
    [onCloseHandler]
  );

  return (
    <>
      <Box className={classes.root}>
        {!isAuthorized && <GuestSidebarHeader onMenuClick={onOpenHandler} />}
        {isAuthorized && (
          <Button
            className={classes.button}
            disableRipple
            fullWidth
            onClick={onOpenHandler}
            startIcon={<UserAvatar user={user as UserEntity} />}
            endIcon={<KeyboardArrowDownIcon />}
            data-testid="user-context-menu-button"
          >
            <Typography className={classes.username}>{user?.userName}</Typography>
          </Button>
        )}
      </Box>
      <Menu
        marginThreshold={0}
        open={open}
        anchorEl={anchorEl}
        className={classes.menu}
        onClose={onCloseHandler}
      >
        {nonAuthorizedActionsMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <SidebarItem
                className={classes.navigationItem}
                item={item}
                onClick={onClickHandler}
              />
              <Divider />
            </MenuItem>
          </Box>
        ))}
        {myProfileMenu.map((item) => (
          <MenuItem key={item.label}>
            <ActionGuard disabled={item?.isAllowedWithoutAuthorization}>
              <SidebarItem
                item={item}
                onClick={onClickHandler}
                className={classes.navigationItem}
              />
            </ActionGuard>
          </MenuItem>
        ))}
        <Divider />
        {linksMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <SidebarMenuLink item={item} onClick={onClickHandler} />
              <Divider />
            </MenuItem>
          </Box>
        ))}
        {authorizedActionsMenu.map((item) => (
          <Box key={item.label}>
            <MenuItem className={classes.control}>
              <SidebarItem
                className={classes.navigationItem}
                item={item}
                onClick={onClickHandler}
              />
              <Divider />
            </MenuItem>
          </Box>
        ))}
      </Menu>
      {isLogoutOpen && <LogoutPopup isOpen={isLogoutOpen} toggle={onLogoutHandler} />}
    </>
  );
};
